import { computed, observable, action, makeObservable } from 'mobx';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { SelectState } from 'src/domains/players/webview/components/form/select/Select.state';
import { SelectOptionType } from 'src/domains/players/webview/components/form/select/Select';
import { PopupInfo, SignupPopupState } from 'src/domains/players/state/SignupPopupState';
import { getRealityCheck, putRealityCheck } from 'src_server/trpc/types/responsibleGambling';
import { ResourceCustomer } from 'src/domains/players/shared/Types';
import { Common } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';

export class RealityCheckState {
    @observable public readonly selectedDuration: SelectState;
    @observable public isSwitched: boolean = false;
    @observable public infoMessage: string = '';
    private readonly realityCheckData: ResourceCustomer<getRealityCheck.SuccessResponseType | null>;
    private readonly language: LanguagesState;
    private readonly configComponents: ConfigComponents;

    public constructor(public readonly common: Common) {
        makeObservable(this);

        this.realityCheckData = new ResourceCustomer(
            'realityCheckData',
            common.session,
            async () => {
                const response = await this.common.trpcClient.client.responsibleGamblingRouter.getRealityCheck.query();

                if (response.responseStatus === 'success') {
                    return response.data;
                }

                return null;
            },
            { allowEphemeral: true }
        );

        this.language = LanguagesState.get(this.common);
        this.configComponents = ConfigComponents.get(this.common);
        const duration =
            this.activeDuration !== undefined && this.activeDuration !== null
                ? this.realityCheckOptions[this.activeDuration]
                : undefined;
        this.selectedDuration = new SelectState(duration);
    }

    @computed public get realityCheckOptions(): Array<SelectOptionType> {
        const isLimited = this.configComponents.config.realityCheckLimitedType;
        if (isLimited) {
            return [
                { '30': this.language.getTranslation('signup.reality-check.30', '30 Minutes') },
                { '60': this.language.getTranslation('signup.reality-check.60', '1 Hour') },
                { '90': this.language.getTranslation('signup.reality-check.90', '90 Minutes') },
            ];
        }
        return [
            { '30': this.language.getTranslation('signup.reality-check.30', '30 Minutes') },
            { '60': this.language.getTranslation('signup.reality-check.60', '1 Hour') },
            { '90': this.language.getTranslation('signup.reality-check.90', '90 Minutes') },
            { '120': this.language.getTranslation('signup.reality-check.120', '2 Hours') },
            { '180': this.language.getTranslation('signup.reality-check.180', '3 Hours') },
            { '300': this.language.getTranslation('signup.reality-check.300', '5 Hours') },
        ];
    }

    @computed public get realityCheckResponse(): getRealityCheck.SuccessResponseType | undefined {
        const data = this.realityCheckData.valueReady;

        if (data === null) {
            return undefined;
        }
        return data;
    }

    @computed public get activeDuration(): number | null | undefined {
        if (this.realityCheckResponse === undefined) {
            return undefined;
        }

        const value = this.realityCheckResponse.active;
        if (value !== undefined && value !== null) {
            return value.duration;
        }

        return undefined;
    }

    @action public onSubmitRealityCheckForm = async (): Promise<void> => {
        if (this.selectedDuration.value === undefined) {
            this.infoMessage = 'empty';
            return undefined;
        }
        try {
            const selectedDurationKey = Object.keys(this.selectedDuration.value)[0];

            if (selectedDurationKey === undefined) {
                throw new Error();
            }

            await this.setRealityCheck({
                status: this.activeDuration === undefined ? 'active' : 'pending',
                body: {
                    duration: parseInt(selectedDurationKey, 10),
                },
            });
            this.infoMessage = 'success';
        } catch (e) {
            console.error(e);
            this.infoMessage = 'error';
        }
    };

    public onChange = (): void => {
        this.infoMessage = '';
    };

    public onSwitch = (): void => {
        this.isSwitched = !this.isSwitched;
    };

    public async setRealityCheck(
        params: putRealityCheck.InputType
    ): Promise<putRealityCheck.SuccessResponseType | null> {
        const response = await this.common.trpcClient.client.responsibleGamblingRouter.putRealityCheck.mutate(params);

        if (response.responseStatus === 'success') {
            this.realityCheckData.refresh();
            return response.data;
        }

        return null;
    }
}

export class RealityCheckPopupState {
    public constructor(
        private readonly signupPopupState: SignupPopupState,
        public readonly language: LanguagesState
    ) {}

    public renderRealityCheckPopup = (): void => {
        const popUpTitle = this.language.getTranslation(
            'signup-journey.popups.realitycheck-popup-title',
            'Reality check'
        );
        const popUpText = this.language.getTranslation(
            'signup-journey.popups.realitycheck-popup-text',
            'A reality check is a notification that interrupts your session to discretely remind you how long you’ve been playing for.'
        );
        const popup = new PopupInfo(popUpTitle, false, popUpText);
        this.signupPopupState.show(popup);
    };
}
